import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { authHeaders } from "@/lib/authToken";

/**
 * DS EDIT 3 — Creative Studio image catalog (generated_images + private
 * 'images' bucket, migration 070). Generation itself goes through the server
 * lane POST /api/ai/images/generate; this service handles listing, signed
 * URLs (1h), favorite/tags, and delete (row + storage object — Art. 17).
 */

export interface GeneratedImage {
  id: string;
  prompt: string;
  model: string;
  storagePath: string;
  width: number | null;
  height: number | null;
  tags: string[];
  isFavorite: boolean;
  createdAt: string;
  /** resolved signed URL (1h expiry) — populated by withSignedUrls */
  url?: string;
}

export interface GenerateImageResult {
  id: string;
  storage_url: string | null;
  prompt: string;
  model: string;
  width: number;
  height: number;
  created_at: string;
  crisis_language?: boolean;
}

const TABLE = "generated_images";
const BUCKET = "images";

class GeneratedImagesService {
  /** Generate through the Deeply Smart server lane (never a provider directly). */
  async generate(input: {
    prompt: string;
    model?: string;
    size?: "1024x1024" | "1792x1024" | "1024x1792";
    style?: "vivid" | "natural";
  }): Promise<GenerateImageResult> {
    const headers = await authHeaders();
    const res = await fetch("/api/ai/images/generate", {
      method: "POST",
      headers,
      body: JSON.stringify(input),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((body as { error?: string }).error ?? `Generation failed (${res.status})`);
    return body as GenerateImageResult;
  }

  async list(): Promise<GeneratedImage[]> {
    if (!isSupabaseConfigured) return [];
    const { data, error } = await supabase
      .from(TABLE)
      .select("id, prompt, model, storage_path, width, height, tags, is_favorite, created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: Record<string, unknown>) => ({
      id: String(r.id),
      prompt: String(r.prompt ?? ""),
      model: String(r.model ?? ""),
      storagePath: String(r.storage_path ?? ""),
      width: (r.width as number) ?? null,
      height: (r.height as number) ?? null,
      tags: (r.tags as string[]) ?? [],
      isFavorite: Boolean(r.is_favorite),
      createdAt: String(r.created_at ?? ""),
    }));
  }

  /** Resolve signed URLs in one batch (private bucket; 1h expiry). */
  async withSignedUrls(images: GeneratedImage[]): Promise<GeneratedImage[]> {
    if (!images.length) return images;
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(images.map((i) => i.storagePath), 3600);
    if (error || !data) return images;
    const byPath = new Map(data.map((d) => [d.path, d.signedUrl]));
    return images.map((i) => ({ ...i, url: byPath.get(i.storagePath) ?? undefined }));
  }

  async setFavorite(id: string, isFavorite: boolean): Promise<void> {
    const { error } = await supabase.from(TABLE).update({ is_favorite: isFavorite, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) throw new Error(error.message);
  }

  async setTags(id: string, tags: string[]): Promise<void> {
    const { error } = await supabase.from(TABLE).update({ tags, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) throw new Error(error.message);
  }

  /** Art. 17: remove BOTH the catalog row and the storage object. */
  async deleteImage(img: Pick<GeneratedImage, "id" | "storagePath">): Promise<void> {
    await supabase.storage.from(BUCKET).remove([img.storagePath]);
    const { error } = await supabase.from(TABLE).delete().eq("id", img.id);
    if (error) throw new Error(error.message);
  }
}

export const generatedImagesService = new GeneratedImagesService();

export const generatedImagesQueryKey = (userId: string | undefined | null) => ["generated-images", userId ?? "anon"];
